import React from 'react';
import { Link } from 'react-router-dom';
import {
  createBluetoothSignal,
  createWebAddressSignal,
  lifeTaggerApi,
  normalizeBluetoothUuid,
  normalizeWebAddress,
} from '../api/lifetaggerApi';
import './LocationSignalCreationWorkspace.css';
import './AddressAndBeaconCreationWorkspace.css';

const pageSize = 8;

function Header() {
  return (
    <header className="location-topbar">
      <Link className="location-brand" to="/objects">
        <img src="/images/lifeTagger_logo.png" alt="" />
        <span>LifeTagger</span>
      </Link>
      <nav className="location-nav">
        <div className="location-switcher">
          <Link to="/objects">Collection</Link>
          <Link className="active" to="/signals">
            Signals
          </Link>
        </div>
        <span>Activity</span>
        <span>Settings</span>
      </nav>
      <div className="location-account">
        <span>⌕</span>
        <span>♢</span>
        <b>LT</b>
      </div>
    </header>
  );
}

function Progress({ steps, current }) {
  return (
    <div
      className="location-progress"
      aria-label={`Step ${current} of ${steps.length}`}
    >
      {steps.map((label, index) => (
        <div className={index + 1 <= current ? 'complete' : ''} key={label}>
          <span>{index + 1}</span>
          <strong>{label}</strong>
          {index < steps.length - 1 && <i />}
        </div>
      ))}
    </div>
  );
}

const thumbnailFor = (item) =>
  item.content?.find((content) => content.type === 'image' && content.url)?.url;

function Connections({ selectedIds, setSelectedIds }) {
  const [items, setItems] = React.useState([]);
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    lifeTaggerApi
      .listLifeObjectsPage({ page: 1, limit: 100 })
      .then((result) => setItems(result.items || []))
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((item) =>
    item.name?.toLowerCase().includes(query.trim().toLowerCase())
  );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
  const toggle = (id) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <section className="location-card location-connect">
      <div className="location-connect-heading">
        <div>
          <h2>Choose LifeObjects</h2>
          <p>You can change these connections later.</p>
        </div>
        <span>Connected ({selectedIds.size})</span>
      </div>
      <div className="primitive-additive">
        <strong>Every connected signal must match.</strong>
        <span>
          If a LifeObject uses other signals too, a visitor must satisfy all of
          them before it appears.
        </span>
      </div>
      <input
        className="location-object-search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setPage(1);
        }}
        placeholder="Search LifeObjects…"
      />
      {loading && <div className="location-loading">Loading LifeObjects…</div>}
      {error && <div className="location-error">{error}</div>}
      {!loading && !visible.length && (
        <div className="location-loading">
          No LifeObjects match this search.
        </div>
      )}
      {visible.map((item) => {
        const selected = selectedIds.has(item.id);
        return (
          <button
            className={`location-object-row ${selected ? 'selected' : ''}`}
            onClick={() => toggle(item.id)}
            key={item.id}
          >
            <span className="location-object-thumb">
              {thumbnailFor(item) ? (
                <img src={thumbnailFor(item)} alt="" />
              ) : (
                '◇'
              )}
            </span>
            <strong>{item.name}</strong>
            <span>
              {item.lifecycle?.status === 'active' ? 'Published' : 'Draft'}
            </span>
            <b>{selected ? 'Connected ✓' : 'Connect'}</b>
          </button>
        );
      })}
      <div className="location-object-pagination">
        <span>
          Showing {visible.length} of {filtered.length}
        </span>
        <div>
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <strong>
            {page} of {pages}
          </strong>
          <button disabled={page === pages} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default function AddressAndBeaconCreationWorkspace({ history, mode }) {
  const isWeb = mode === 'url';
  const steps = isWeb
    ? ['Details', 'Connect', 'Review']
    : ['Beacon details', 'Connect', 'Review'];
  const [step, setStep] = React.useState(1);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [includeQuery, setIncludeQuery] = React.useState(false);
  const [uuid, setUuid] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [created, setCreated] = React.useState(null);
  const normalizedAddress = normalizeWebAddress(address, { includeQuery });
  const normalizedUuid = normalizeBluetoothUuid(uuid);
  const dirty = Boolean(
    name || description || address || uuid || selectedIds.size
  );

  React.useEffect(() => {
    const warn = (event) => {
      if (!dirty || created) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, created]);

  const leave = () => {
    if (
      !dirty ||
      window.confirm('Leave creation? Your unsaved changes will be lost.')
    )
      history.push('/signals');
  };
  const next = () => {
    setError('');
    if (step === 1) {
      if (!name.trim())
        return setError(
          `Give this ${isWeb ? 'web address signal' : 'beacon'} a clear name.`
        );
      if (isWeb && !normalizedAddress)
        return setError(
          'Enter a complete public address beginning with http:// or https://.'
        );
      if (!isWeb && !normalizedUuid)
        return setError(
          'Enter the complete physical beacon UUID in 8-4-4-4-12 format.'
        );
    }
    setStep((current) => Math.min(3, current + 1));
  };
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const result = isWeb
        ? await createWebAddressSignal({
            name: name.trim(),
            description: description.trim(),
            url: address,
            includeQuery,
          })
        : await createBluetoothSignal({
            name: name.trim(),
            description: description.trim(),
            beaconId: uuid,
          });
      const anchor = result.anchor;
      const links = await Promise.allSettled(
        [...selectedIds].map((id) => lifeTaggerApi.linkAnchor(id, anchor.id))
      );
      const failures = links.filter(
        (link) => link.status === 'rejected'
      ).length;
      setCreated({
        anchor,
        existing: result.existing,
        connectedCount: selectedIds.size - failures,
      });
      if (failures)
        setError(
          `${failures} LifeObject connection could not be saved. The signal was still created.`
        );
    } catch (requestError) {
      setError(
        requestError.message ||
          `The ${
            isWeb ? 'web address signal' : 'Bluetooth beacon'
          } could not be saved.`
      );
    } finally {
      setSaving(false);
    }
  };

  if (created)
    return (
      <div className="location-page">
        <Header />
        <main className="location-success">
          <span className="location-success-mark">✓</span>
          <p className="location-crumb">
            Signals /{' '}
            {isWeb ? 'Web address created' : 'Bluetooth beacon registered'}
          </p>
          <h1>
            {isWeb
              ? 'This address can now reveal LifeObjects'
              : 'Your beacon is ready to be discovered'}
          </h1>
          <p>
            <strong>{name}</strong> is active and connected to{' '}
            {created.connectedCount} LifeObject
            {created.connectedCount === 1 ? '' : 's'}.
          </p>
          {created.existing && (
            <div className="primitive-notice">
              The existing signal for this address was reused, so no duplicate
              was created.
            </div>
          )}
          {error && <div className="location-error">{error}</div>}
          <Summary
            isWeb={isWeb}
            name={name}
            description={description}
            value={isWeb ? normalizedAddress : normalizedUuid}
            includeQuery={includeQuery}
          />
          <div className="location-success-actions">
            <button onClick={() => history.push('/signals')}>
              View in Signals
            </button>
            <button
              className="primary"
              onClick={() => window.location.reload()}
            >
              {isWeb ? 'Create another' : 'Register another'}
            </button>
          </div>
        </main>
      </div>
    );

  return (
    <div className="location-page">
      <Header />
      <main className="location-workspace">
        <p className="location-crumb">
          Signals /{' '}
          {isWeb ? 'New web address signal' : 'Register Bluetooth beacon'}
        </p>
        <h1>
          {step === 1
            ? isWeb
              ? 'Match a trusted web address'
              : 'Register the beacon you have'
            : step === 2
            ? 'Connect the experience'
            : isWeb
            ? 'Review this web address signal'
            : 'Review this Bluetooth beacon'}
        </h1>
        <p className="location-intro">
          {step === 1
            ? isWeb
              ? 'Name the signal, enter its public address, and decide exactly which visits count.'
              : 'Use the UUID printed on the beacon, its packaging, or the hardware provider’s record.'
            : step === 2
            ? 'Choose which LifeObjects visitors can discover through this signal.'
            : 'Confirm the discovery experience before making this signal active.'}
        </p>
        <Progress steps={steps} current={step} />
        {error && (
          <div className="location-error" role="alert">
            {error}
          </div>
        )}
        {step === 1 &&
          (isWeb ? (
            <WebDetails
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              address={address}
              setAddress={setAddress}
              includeQuery={includeQuery}
              setIncludeQuery={setIncludeQuery}
              normalized={normalizedAddress}
            />
          ) : (
            <BeaconDetails
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              uuid={uuid}
              setUuid={setUuid}
              normalized={normalizedUuid}
            />
          ))}
        {step === 2 && (
          <Connections
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
        {step === 3 && (
          <div className="location-columns">
            <Summary
              isWeb={isWeb}
              name={name}
              description={description}
              value={isWeb ? normalizedAddress : normalizedUuid}
              includeQuery={includeQuery}
            />
            <aside className="location-note green">
              <h2>Ready to {isWeb ? 'create' : 'register'}</h2>
              <p>
                This will make the signal active and connect {selectedIds.size}{' '}
                LifeObject{selectedIds.size === 1 ? '' : 's'}.
              </p>
              {!isWeb && (
                <p>
                  LifeTagger will register the hardware record and its discovery
                  signal together. A safe retry will repair either missing part.
                </p>
              )}
            </aside>
          </div>
        )}
        <footer className="location-footer">
          <button onClick={step === 1 ? leave : () => setStep(step - 1)}>
            Back
          </button>
          <div>
            <small>Changes stay in this draft until creation</small>
            <button
              className="primary"
              disabled={saving}
              onClick={step === 3 ? save : next}
            >
              {saving
                ? isWeb
                  ? 'Creating…'
                  : 'Registering…'
                : step === 3
                ? isWeb
                  ? 'Create web address signal'
                  : 'Register Bluetooth beacon'
                : `${steps[step]} →`}
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
}

function WebDetails({
  name,
  setName,
  description,
  setDescription,
  address,
  setAddress,
  includeQuery,
  setIncludeQuery,
  normalized,
}) {
  return (
    <div className="location-columns">
      <section className="location-card">
        <label>
          Signal name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Membership welcome page"
          />
          <small>Visible to collaborators in Signals.</small>
        </label>
        <label>
          Internal description (optional)
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What should collaborators know about this address?"
          />
        </label>
        <label>
          Complete public web address
          <input
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder="https://garden.org/members/welcome"
          />
          <small>
            {address
              ? normalized
                ? `Normalized preview: ${normalized}`
                : 'Use a complete http:// or https:// address.'
              : 'Enter the address contextual web should recognize.'}
          </small>
        </label>
        <div className="primitive-match">
          <h2>What should count as a match?</h2>
          <div className="primitive-choice selected">
            <strong>Exact address</strong>
            <span>
              Only this page counts. This is the supported, safest behavior.
            </span>
          </div>
          <label className="primitive-toggle">
            <input
              type="checkbox"
              checked={!includeQuery}
              onChange={(event) => setIncludeQuery(!event.target.checked)}
            />
            <span>
              <strong>Ignore tracking details</strong>
              <small>
                Values after ? such as campaign tags do not change the match.
              </small>
            </span>
          </label>
        </div>
      </section>
      <aside className="primitive-states">
        {normalized ? (
          <div className="primitive-state success">
            <strong>Address ready</strong>
            <span>
              Uses {normalized.startsWith('https:') ? 'HTTPS' : 'HTTP'} and will
              use exact address matching.
            </span>
            <a href={normalized} target="_blank" rel="noopener noreferrer">
              Confirm destination ↗
            </a>
          </div>
        ) : (
          <div className="primitive-state danger">
            <strong>Unsupported address</strong>
            <span>
              Use a complete address beginning with http:// or https://. Email,
              file, and app links are not supported.
            </span>
          </div>
        )}
        <div className="primitive-state warning">
          <strong>If the page cannot be reached</strong>
          <span>
            You can still create the signal when the address is correct; the
            page may be private or temporarily unavailable.
          </span>
        </div>
      </aside>
    </div>
  );
}

function BeaconDetails({
  name,
  setName,
  description,
  setDescription,
  uuid,
  setUuid,
  normalized,
}) {
  return (
    <div className="location-columns">
      <section className="location-card">
        <label>
          Beacon name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Orchid House entrance beacon"
          />
        </label>
        <label>
          Internal description (optional)
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Where is this beacon installed?"
          />
        </label>
        <label>
          Physical beacon UUID
          <input
            className="primitive-mono"
            value={uuid}
            onChange={(event) => setUuid(event.target.value)}
            onBlur={() => normalized && setUuid(normalized)}
            placeholder="A3F2C918-7B64-4D12-9E0A-3C51B6680F24"
          />
          <small>
            Paste is supported. Letters become uppercase and standard hyphens
            are restored without changing the beacon’s identity.
          </small>
        </label>
        <div className="primitive-state success">
          <strong>Where to find it</strong>
          <span>
            Look for UUID on the beacon label, box, or provider dashboard.
            LifeTagger does not generate or configure beacon hardware.
          </span>
        </div>
      </section>
      <aside className="primitive-states">
        {normalized ? (
          <div className="primitive-state success">
            <strong>Valid UUID</strong>
            <span className="primitive-mono">{normalized}</span>
          </div>
        ) : (
          <div className="primitive-state danger">
            <strong>{uuid ? 'Check the format' : 'UUID required'}</strong>
            <span>
              {uuid
                ? 'A UUID has 32 letters and numbers shown in five groups: 8-4-4-4-12.'
                : 'Enter the physical beacon UUID to continue.'}
            </span>
          </div>
        )}
        <div className="primitive-state warning">
          <strong>Already registered?</strong>
          <span>
            LifeTagger will reuse a beacon registered to this account and repair
            a missing discovery signal instead of creating a duplicate.
          </span>
        </div>
      </aside>
    </div>
  );
}

function Summary({ isWeb, name, description, value, includeQuery }) {
  return (
    <section className="location-card location-summary">
      <h2>{name}</h2>
      {description && <p>{description}</p>}
      <dl>
        <div>
          <dt>{isWeb ? 'Public web address' : 'Physical beacon UUID'}</dt>
          <dd className={isWeb ? '' : 'primitive-mono'}>{value}</dd>
        </div>
        <div>
          <dt>Detection</dt>
          <dd>
            {isWeb
              ? `Exact address · ${
                  includeQuery
                    ? 'query details matter'
                    : 'tracking details ignored'
                }`
              : 'Bluetooth beacon detected by UUID'}
          </dd>
        </div>
      </dl>
    </section>
  );
}
